import { type Dog } from '@/api/entities/dogs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Label } from './label';
import { Skeleton } from './skeleton';
import { ReactNode } from 'react';
import { useDogs } from '@/features/dogs/api/useDogs';

type DogSelectProps = {
  value?: string; // Dog ID
  onValueChange: (value: string) => void;
  sex?: string; // 'Male' or 'Female'
  label?: string | ReactNode;
  placeholder?: string;
};

export function DogSelect({
  value,
  onValueChange,
  sex,
  label,
  placeholder = 'Select a dog',
}: DogSelectProps) {
  const { data: dogs, isLoading } = useDogs({
    queryParams: { sex },
    config: {
      staleTime: 60_000,
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {label && <Label>{label}</Label>}
        <Skeleton className="h-9 w-full" role="status" aria-label="Loading dogs" />
      </div>
    );
  }

  const options = (dogs || []) as Dog[];

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((dog) => (
            <SelectItem key={dog.id} value={dog.id}>
              {dog.name}
              {dog.longName && ` (${dog.longName})`}
            </SelectItem>
          ))}
          {options.length === 0 && (
            <SelectItem value="no-dogs" disabled>
              No dogs available
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
